import { isVideoConnection } from './equipment';
import { isSuitestreamInstalledApp } from '../components/Remote/installedAppSelection';
import { evidenceFresh } from './evidenceFreshness';
import { resolvePowerObservations } from './powerObservations';
import { channelSnapshotFresh } from './channelFreshness';

export { evidenceFresh } from './evidenceFreshness';

export function getDeviceClockStatus(liveState) {
  const state = liveState?.state || {};
  const externalApp = state.externalApp || {};
  const collectedAt = Number(externalApp.collectedAt);
  const receivedAt = Number(externalApp.lastTelemetryAt || state.lastExternalTelemetryAt);
  if (!Number.isFinite(collectedAt) || collectedAt <= 0 || !Number.isFinite(receivedAt) || receivedAt <= 0) {
    return { state: 'unknown', label: 'Device clock: comparison unavailable' };
  }
  // Compare against server receipt, not the browser clock. Transit delay affects this estimate.
  const offset = collectedAt - receivedAt;
  const difference = `${(Math.abs(offset) / 1000).toFixed(1)}s ${offset >= 0 ? 'ahead of' : 'behind'} server receipt`;
  if (offset > 2000 || offset < -20000) {
    return { state: 'warning', label: `Device clock / telemetry timing outside power-control limits: ${difference}. Check automatic date and time on the device and its connection.` };
  }
  return { state: 'acceptable', label: `Device clock / telemetry timing acceptable: ${difference} (up to 2s ahead or 20s old allowed).` };
}

export function getDeviceOperationalState(liveState, now = Date.now()) {
  const state = liveState?.state || {};
  const externalApp = state.externalApp || {};
  const channels = state.channels || {};
  const clock = getDeviceClockStatus(liveState);
  const collectedAt = Number(externalApp.collectedAt);
  // A cached isStale flag ages between socket updates; use the routine's display evidence limits.
  const telemetryFresh = clock.state !== 'warning' && externalApp.isStale !== true
    && Number.isFinite(collectedAt) && collectedAt > 0
    && collectedAt <= now + 2000 && now - collectedAt <= 20000;
  let power = { state: 'unknown', label: 'Display unknown' };
  if (telemetryFresh && externalApp.dreaming === true) {
    power = { state: 'dreaming', label: 'Screensaver / dreaming' };
  } else if (telemetryFresh && externalApp.displayInteractive === true) {
    power = { state: 'awake', label: 'Display awake' };
  } else if (telemetryFresh && externalApp.displayInteractive === false) {
    power = { state: 'asleep', label: 'Display asleep / standby' };
  }
  const channelFresh = channelSnapshotFresh(liveState, now);
  const reachable = channelFresh && liveState?.available !== false;
  const nativeConnected = reachable && channels.native?.connected === true;
  const playerConnected = reachable && channels.player?.connected === true;
  const monitoringTime = Number(channels.monitoring?.observedAt || state.lastExternalTelemetryAt || externalApp.collectedAt);
  const monitoringFresh = Number.isFinite(monitoringTime) && monitoringTime > 0
    && monitoringTime <= now + 2000 && now - monitoringTime <= 60000;
  const monitoringConnected = nativeConnected && monitoringFresh && (channels.monitoring?.connected === true
    || (channels.monitoring?.connected == null && externalApp.socketConnected === true));
  const channelLabel = (name, channel, connected) => !channelFresh || typeof channel?.connected !== 'boolean'
    ? `${name} status unknown` : connected ? `${name} connected` : `${name} not connected`;
  return {
    power,
    native: {
      connected: nativeConnected,
      label: channelLabel('Suitestream service', channels.native, nativeConnected),
    },
    player: {
      connected: playerConnected,
      label: channelLabel('Suitestream player', channels.player, playerConnected),
    },
    monitoring: {
      connected: monitoringConnected,
      label: monitoringConnected ? 'App monitoring connected' : 'App monitoring unavailable',
    },
    foreground: externalApp.foregroundAppLabel || externalApp.foregroundPackageName || null,
    telemetryFresh,
    clock,
  };
}

export function hdmiPort(value) {
  const match = String(value || '').match(/\bHDMI[\s_-]*(\d+)\b/i);
  return match ? Number(match[1]) : null;
}

export function inputEvidenceFresh(report, now = Date.now()) {
  if (report?.isStale === true) return false;
  // Inventory and selected-input observations are independently collected.
  return evidenceFresh(report?.activeSource, now) || (
    report?.activeSource?.collectedAt == null && evidenceFresh(report, now)
  );
}

function usableActiveSource(source) {
  if (!source || source.available !== true) return null;
  if (Number.isInteger(Number(source.port)) && Number(source.port) > 0) return source;
  const label = String(source.label ?? '').trim();
  // Some MediaTek providers return a boolean/status column instead of a source
  // (Nhan's TV reports the literal "true"). It cannot veto real foreground evidence.
  if (/^(?:true|false|null|none|undefined|nan|[+-]?\d+(?:\.\d+)?)$/i.test(label)) return null;
  if (!label && !['internal', 'home', 'app', 'android_tv'].includes(source.kind)) return null;
  return source;
}

export function foregroundActivity(live, now = Date.now()) {
  const state = live?.state || {};
  const external = state.externalApp || {};
  const available = live?.available !== false && getDeviceOperationalState(live, now).power.state !== 'asleep';
  const fresh = available && evidenceFresh(external, now);
  const appName = fresh ? external.foregroundAppLabel || '' : '';
  const packageName = fresh ? external.foregroundPackageName || '' : '';
  const known = Boolean(appName || packageName);
  const inputSelector = fresh && /inputs?(?:panel|selector|picker)/i.test(external.foregroundClassName || '');
  const home = !inputSelector && /launcher|home screen|tv home/i.test(`${appName} ${packageName}`);
  const generic = /^(tv|live tv|television|input|hdmi[\s_-]*\d+)$/i.test((appName || packageName).trim());
  const isSuitestream = fresh && known && isSuitestreamInstalledApp({ appName, packageName });
  const playerFresh = available && evidenceFresh({ collectedAt: state.lastPlayerStateAt }, now, 30000);
  // Deck reports describe content changes, not a heartbeat. Keep the current
  // content while fresh foreground telemetry confirms the connected player.
  const playerCurrent = playerFresh || (available && isSuitestream && state.channels?.player?.connected === true
    && evidenceFresh({ collectedAt: state.lastPlayerStateAt }, now, Infinity));
  // A fresh explicit foreground app wins over a lagging interfaceMode and hidden Deck A.
  const suitestream = available && (isSuitestream || (!known && !external.foregroundAppLabel && !external.foregroundPackageName
    && playerFresh && state.interfaceMode === 'suitestream'));
  const deck = suitestream && playerCurrent ? state.deckA : null;
  const artworkUrl = deck?.thumbnailUrl || (deck?.type === 'youtube' && deck.videoId
    ? `https://i.ytimg.com/vi/${deck.videoId}/hqdefault.jpg` : null);
  return {
    title: suitestream ? deck?.title || 'Suitestream' : inputSelector ? 'TV input selector' : home ? 'Home Screen' : appName || packageName || null,
    appName, packageName, home, inputSelector, generic, known, fresh, artworkUrl,
    isSuitestream: suitestream && playerCurrent,
    isExternalApp: fresh && known && !isSuitestream,
    isSpotify: fresh && (/^spotify$/i.test(appName) || /^com\.spotify\./i.test(packageName)),
    mixDisabled: state.isMixing === true || deck?.type === 'event',
    inferredPlayer: !known && suitestream,
  };
}

export function getScreenNowPlaying(screen, devicesById, liveStates, report, now = Date.now(), powerDetails = {}) {
  const television = devicesById.get(Number(screen.televisionDeviceId));
  const tvLive = liveStates[television?.id];
  const power = resolvePowerObservations(tvLive, powerDetails[television?.id], now);
  const display = power.fields.displayState?.value;
  const dreaming = power.fields.dreaming?.value === true;
  const dozing = power.fields.dozing?.value === true || ['DOZE', 'DOZE_SUSPEND'].includes(display);
  // Presentation only. Routine planning and toggle authorization keep their existing evidence.
  if (power.disagreements.length) return { title: 'Power observations disagree',
    source: 'Native and ADB observations differ · see Power details', confirmed: false, activeDeviceId: null, screenUnavailable: true };
  if (dozing || dreaming) return { title: dozing ? 'Display dozing' : 'Screensaver / dreaming',
    source: `${television?.name || screen.name} · display ${display || 'unknown'} · current media unverified`,
    confirmed: false, activeDeviceId: null };
  if (display === 'ON' && power.fields.interactive?.value === false) return { title: 'TV non-interactive',
    source: `${television?.name || screen.name} reports display ON · current media unverified`, confirmed: false, activeDeviceId: null };
  const tvPower = display === 'OFF' ? 'asleep' : display === 'ON' ? 'awake' : getDeviceOperationalState(tvLive, now).power.state;
  if (tvPower === 'asleep') {
    return { title: 'Screen off', source: `${television?.name || screen.name} is asleep or in standby · nothing visible`,
      confirmed: false, activeDeviceId: null, screenOff: true };
  }
  const channels = tvLive?.state?.channels || {};
  const adbFresh = evidenceFresh(power.adb, now);
  const disconnected = !adbFresh && (tvLive?.available === false || (channels.native?.connected === false
    && channels.player?.connected === false && channels.monitoring?.connected === false));
  if (screen.displayKind !== 'unmanaged' && (tvPower === 'unknown' || disconnected)) {
    return { title: disconnected ? 'Screen unreachable' : 'Screen status unknown',
      source: disconnected
        ? `${television?.name || screen.name} is unreachable · physical power unknown; source playback does not confirm a visible picture`
        : 'Waiting for TV display status · source playback does not confirm a visible picture',
      confirmed: false, activeDeviceId: null, screenUnavailable: true };
  }
  const videoPlayers = (screen.players || []).filter((player) => (
    isVideoConnection(player, devicesById.get(Number(player.deviceId || player.id)))
  ));
  const active = inputEvidenceFresh(report, now) ? usableActiveSource(report?.activeSource) : null;
  const port = active && Number.isInteger(Number(active.port)) && Number(active.port) > 0 ? Number(active.port) : null;
  if (port) {
    const matches = videoPlayers.filter((player) => hdmiPort(player.expectedInput) === port);
    const device = matches.length === 1 && devicesById.get(Number(matches[0].deviceId || matches[0].id));
    const activity = device ? foregroundActivity(liveStates[device.id], now) : null;
    const live = liveStates[device?.id];
    const sourcePower = device && resolvePowerObservations(live, powerDetails[device.id], now);
    const sourceDisplay = sourcePower?.fields?.displayState?.value;
    const asleep = device && (sourceDisplay === 'OFF' || (!sourceDisplay && getDeviceOperationalState(live, now).power.state === 'asleep'));
    const offline = live?.available === false;
    return { title: asleep ? 'Source asleep / standby' : offline ? 'Source unreachable' : activity?.title || 'App unknown',
      source: device ? `${device.name || `Device ${device.id}`} on HDMI ${port}`
        : `${matches.length > 1 ? 'Ambiguous mapping' : 'Unmapped source'} on HDMI ${port}`,
      // Confirmation describes the selected source, not playback or source reachability.
      confirmed: Boolean(device), activeDeviceId: device?.id || null, activePort: port,
      sourceAsleep: Boolean(asleep), sourceUnavailable: offline,
      artworkUrl: activity?.artworkUrl || null, activity };
  }
  const tvActivity = foregroundActivity(tvLive, now);
  const explicitOtherInput = active && !['internal', 'home', 'app', 'android_tv'].includes(active.kind);
  const tuner = active?.kind === 'internal' && /^(tv|dtv|atv|tuner|live tv)$/i.test(String(active.label || '').trim());
  const nativeApp = !explicitOtherInput && !tuner && tvActivity.known && !tvActivity.generic;
  // A vendor's generic "internal" source alone is not proof of a TV app.
  if (nativeApp) {
    return { title: tvActivity.title, source: tvActivity.inputSelector
      ? `TV input selector on ${television?.name || screen.name} · selected input unverified`
      : `TV apps on ${television?.name || screen.name}`,
      confirmed: true, activeDeviceId: television?.id || null, artworkUrl: tvActivity.artworkUrl, activity: tvActivity };
  }
  if (active) {
    return { title: tuner ? 'TV tuner' : 'TV source unknown',
      source: `TV reports ${active.label || active.kind || 'an unidentified source'} · app unverified`,
      confirmed: false, activeDeviceId: null };
  }
  const candidates = videoPlayers.map((player) => {
    const device = devicesById.get(Number(player.deviceId || player.id));
    return { player, device, activity: foregroundActivity(liveStates[device?.id], now) };
  }).filter(({ device, activity }) => device && activity.title && !activity.generic
    && (activity.isExternalApp || activity.isSuitestream));
  if (candidates.length === 1) {
    const { player, device, activity } = candidates[0];
    const playerPort = hdmiPort(player.expectedInput);
    return { title: activity.title,
      source: `${device.name || `Device ${device.id}`}${playerPort ? ` on HDMI ${playerPort}` : ''} · TV input unverified`,
      confirmed: false, activeDeviceId: null, reportedDeviceId: device.id, artworkUrl: activity.artworkUrl, activity };
  }
  return { title: 'Screen status unavailable', source: 'Waiting for current TV activity · TV input unverified',
    confirmed: false, activeDeviceId: null };
}

